import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, LoaderCircle, MessageCircleReply, Radar as RadarIcon, RefreshCw, Send, Sparkles, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  discoverRadar,
  editRadar,
  generateRadar,
  intakeRadar,
  loadRadar,
  publishRadar,
  selectRadar,
  skipRadar,
  type RadarOpportunity,
  type RadarPlatform,
  type RadarQueue,
  type RadarQueues,
} from '../promo/radarApi';
import { C, mono } from '../theme';

const EMPTY: RadarQueues = { opportunities: [], conversations: [], settings: null };

const PLATFORM_LABEL: Record<string, string> = { twitter: 'X', threads: 'Threads', instagram: 'Instagram' };
const MECHANIC_LABEL: Record<string, string> = { dry: 'Сухо', twist: 'С поворотом', useful: 'С пользой' };
const PLATFORMS: RadarPlatform[] = ['twitter', 'threads', 'instagram'];

function timeLeft(value: string): string {
  const minutes = Math.round((new Date(value).getTime() - Date.now()) / 60_000);
  if (minutes <= 0) return 'истекло';
  if (minutes < 60) return `${minutes} мин`;
  return `${Math.round(minutes / 60)} ч`;
}

function OpportunityCard({ row, onChanged }: { row: RadarOpportunity; onChanged: (row: RadarOpportunity) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(row.selected_text ?? '');
  useEffect(() => setText(row.selected_text ?? ''), [row.selected_text]);

  const run = async (key: string, action: () => Promise<RadarOpportunity>) => {
    setBusy(key);
    try { onChanged(await action()); } finally { setBusy(null); }
  };

  return (
    <article style={{ border: `1px solid ${C.line2}`, borderRadius: 14, padding: 16, background: C.white, display: 'grid', gap: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7 }}>
        <Badge variant="outline">{PLATFORM_LABEL[row.platform]}</Badge>
        <Badge variant={row.queue_type === 'conversation' ? 'secondary' : 'outline'}>{row.queue_type === 'conversation' ? 'Диалог' : 'Возможность'}</Badge>
        <span style={mono({ fontSize: 10.5, color: C.muted })}>оценка {row.score}</span>
        <span style={mono({ fontSize: 10.5, color: row.risk_score >= 60 ? '#a63828' : C.muted })}>риск {row.risk_score}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: C.faint }}>окно {timeLeft(row.expires_at)}</span>
      </div>

      <div>
        {row.author_username && <div style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>@{row.author_username}</div>}
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.52, color: C.ink2 }}>{row.source_text || 'Текст не получен. Проверьте оригинал перед публикацией.'}</div>
      </div>

      {row.variants.length > 0 && (
        <div style={{ display: 'grid', gap: 7 }}>
          {row.variants.map((variant, index) => {
            const selected = row.selected_text === variant.text;
            return (
              <button
                key={variant.id}
                onClick={() => run(`select-${index}`, () => selectRadar(row.id, index))}
                style={{ textAlign: 'left', border: `1px solid ${selected ? C.ink : C.line3}`, borderRadius: 10, padding: '10px 12px', background: selected ? C.sidebar : C.white, color: C.ink2, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <span style={mono({ display: 'block', fontSize: 9.5, color: C.faint, marginBottom: 4, textTransform: 'uppercase' })}>{MECHANIC_LABEL[variant.mechanic] ?? variant.mechanic}</span>
                {variant.text}
              </button>
            );
          })}
        </div>
      )}

      {editing && (
        <div style={{ display: 'grid', gap: 7 }}>
          <textarea value={text} onChange={(event) => setText(event.target.value)} rows={4} maxLength={500} className="composer-input" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Отмена</Button>
            <Button size="sm" disabled={!text.trim() || busy !== null} onClick={() => run('edit', async () => { const next = await editRadar(row.id, text); setEditing(false); return next; })}>Сохранить</Button>
          </div>
        </div>
      )}

      {row.failure_reason && <div style={{ color: '#a63828', fontSize: 12.5 }}>{row.failure_reason}</div>}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
        <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => run('generate', () => generateRadar(row.id))}>
          {busy === 'generate' ? <LoaderCircle className="animate-spin" /> : <Sparkles />} Ещё варианты
        </Button>
        <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => setEditing(true)}>Изменить текст</Button>
        <Button size="sm" disabled={busy !== null || (!row.selected_text && row.variants.length === 0)} onClick={async () => {
          setBusy('publish');
          try {
            const result = await publishRadar(row.id);
            onChanged(result.opportunity);
            if (result.assisted) window.open(row.source_url, '_blank', 'noopener,noreferrer');
          } finally { setBusy(null); }
        }}>
          {busy === 'publish' ? <LoaderCircle className="animate-spin" /> : <Send />}
          {row.capability === 'direct' ? 'Опубликовать' : 'Открыть и скопировать'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => run('skip', () => skipRadar(row.id))}><X /> Отклонить</Button>
        <Button size="sm" variant="link" asChild><a href={row.source_url} target="_blank" rel="noreferrer"><ExternalLink /> Оригинал</a></Button>
      </div>
    </article>
  );
}

function Queue({ type, rows, onChanged }: { type: RadarQueue; rows: RadarOpportunity[]; onChanged: (row: RadarOpportunity) => void }) {
  if (!rows.length) return <div style={{ padding: '42px 0', textAlign: 'center', color: C.faint }}>{type === 'opportunity' ? 'Новых возможностей пока нет.' : 'Новых диалогов пока нет.'}</div>;
  return <div style={{ display: 'grid', gap: 12 }}>{rows.map((row) => <OpportunityCard key={row.id} row={row} onChanged={onChanged} />)}</div>;
}

export function RadarScreen({ mobile }: { mobile?: boolean }) {
  const [data, setData] = useState<RadarQueues>(EMPTY);
  const [live, setLive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [platform, setPlatform] = useState<RadarPlatform>('twitter');

  const reload = useCallback(async () => {
    const result = await loadRadar();
    setData(result.data); setLive(result.live); setError(result.error ?? '');
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const onChanged = (next: RadarOpportunity) => setData((current) => ({
    ...current,
    opportunities: current.opportunities.map((row) => row.id === next.id ? next : row).filter((row) => row.status !== 'skipped'),
    conversations: current.conversations.map((row) => row.id === next.id ? next : row).filter((row) => row.status !== 'skipped'),
  }));
  const platformCounts = useMemo(() => Object.fromEntries(PLATFORMS.map((item) => [item, data.opportunities.filter((row) => row.platform === item).length + data.conversations.filter((row) => row.platform === item).length])), [data]);
  const filtered = useMemo(() => ({
    opportunities: data.opportunities.filter((row) => row.platform === platform),
    conversations: data.conversations.filter((row) => row.platform === platform),
  }), [data, platform]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: C.white }}>
      {!mobile && (
        <div style={{ height: 56, flexShrink: 0, borderBottom: `1px solid ${C.line4}`, display: 'flex', alignItems: 'center', gap: 9, padding: '0 22px' }}>
          <RadarIcon size={17} /> <b style={{ fontSize: 14.5 }}>Response Radar</b>
          <Badge variant="outline">{data.settings?.automation_mode === 'auto' ? 'автомат' : 'с подтверждением'}</Badge>
          <Button style={{ marginLeft: 'auto' }} size="sm" variant="outline" disabled={busy} onClick={async () => { setBusy(true); try { await discoverRadar(); await reload(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); } }}>
            <RefreshCw className={busy ? 'animate-spin' : ''} /> Найти сейчас
          </Button>
        </div>
      )}
      <div className="brief-scroll" style={{ overflowY: 'auto', flex: 1, padding: mobile ? '16px' : '22px 24px 32px' }}>
        <div style={{ maxWidth: 920, margin: '0 auto', display: 'grid', gap: 18 }}>
          {(!live || error) && <div style={{ padding: '9px 12px', border: `1px solid ${C.line3}`, borderRadius: 9, color: '#a63828', fontSize: 12.5 }}>{error || 'Не удалось связаться с Radar.'}</div>}

          <div style={{ padding: '12px 14px', border: `1px solid ${C.line}`, borderRadius: 12, background: '#f7fbf8', color: C.ink2, fontSize: 12.5, lineHeight: 1.5 }}>
            <b>Автопоиск включён.</b> Radar проверяет новые возможности и диалоги каждые 25–30 минут, сам готовит ответы и присылает готовые карточки в Telegram.
            {data.settings?.last_discovery_at && <span style={{ color: C.muted }}> Последняя проверка: {new Date(data.settings.last_discovery_at).toLocaleString()}.</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)', gap: 8 }}>
            {PLATFORMS.map((item) => {
              const status = data.platform_status?.[item];
              return <div key={item} style={{ padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 10, fontSize: 12, lineHeight: 1.45 }}>
                <b>{status?.discovery === 'active' ? '🟢' : '🟡'} {PLATFORM_LABEL[item]}</b>
                <div style={{ color: C.muted, marginTop: 3 }}>{status?.message ?? 'Проверяю подключение…'}</div>
              </div>;
            })}
          </div>

          <details style={{ padding: 12, background: C.sidebar, border: `1px solid ${C.line}`, borderRadius: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12.5, color: C.muted }}>Добавить пост вручную — необязательно</summary>
            <form onSubmit={async (event) => { event.preventDefault(); if (!url.trim()) return; setBusy(true); try { const row = await intakeRadar(url, text); setData((current) => ({ ...current, opportunities: [row, ...current.opportunities.filter((item) => item.id !== row.id)] })); setUrl(''); setText(''); } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); } }} style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'minmax(220px, .8fr) minmax(260px, 1.2fr) auto', gap: 8, marginTop: 10 }}>
              <input className="composer-input" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="Ссылка X, Threads или Instagram" />
              <input className="composer-input" value={text} onChange={(event) => setText(event.target.value)} placeholder="Текст/контекст поста, если нужен" />
              <Button type="submit" disabled={busy || !url.trim()}><RadarIcon /> В Radar</Button>
            </form>
          </details>

          <Tabs value={platform} onValueChange={(value) => setPlatform(value as RadarPlatform)}>
            <TabsList>
              {PLATFORMS.map((item) => <TabsTrigger key={item} value={item}>{PLATFORM_LABEL[item]} {platformCounts[item]}</TabsTrigger>)}
            </TabsList>
          </Tabs>

          <Tabs defaultValue="opportunity" key={platform}>
            <TabsList>
              <TabsTrigger value="opportunity"><RadarIcon /> Возможности {filtered.opportunities.length}</TabsTrigger>
              <TabsTrigger value="conversation"><MessageCircleReply /> Диалоги {filtered.conversations.length}</TabsTrigger>
            </TabsList>
            <TabsContent value="opportunity"><Queue type="opportunity" rows={filtered.opportunities} onChanged={onChanged} /></TabsContent>
            <TabsContent value="conversation"><Queue type="conversation" rows={filtered.conversations} onChanged={onChanged} /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
